import { ContactValuesField } from '../components/ContactValuesField.js';
import { FollowUpInput } from '../components/FollowUpInput.js';
import { useAgentCopy } from '../lib/agentCopy.js';
import { agentEditorContext } from '../lib/agentEditor.js';
import { quoteCanEdit, quoteWritePayload } from '../lib/quoteRevision.js';
import { agentApi } from '../lib/agent.js';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type InputHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Eye, History, Plus, Save, Send, SlidersHorizontal, Trash2, X } from 'lucide-react';
import {
  DISCOUNT_TYPES,
  QUOTE_FOLLOW_UP_HOURS,
  QUOTE_VALID_DAYS,
  computeTotals,
  quoteWriteSchema,
  type Quote,
  type QuoteItemInput,
  type MiniSite,
} from '@worki/shared';
import { api, RequestError } from '../lib/api.js';
import { useForm } from '../lib/useForm.js';
import { useAuthenticated } from '../lib/session.js';
import { currentLocale } from '../i18n.js';
import { formatDate, money } from '../lib/format.js';
import { editGroupedNumber, formatGroupedNumber, parseGroupedNumber } from '../lib/groupedNumber.js';
import { ErrorNotice, LoadingScreen, Select } from '../components/ui.js';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { CityInput } from '../components/CityInput.js';
import { CustomerMatchNotice, useCustomerMatch } from '../components/CustomerMatchNotice.js';
import { DictateButton } from '../components/DictateButton.js';
import { DeviceContactPicker } from '../components/DeviceContactPicker.js';
import { VoiceButton } from '../components/VoiceButton.js';
import { SaveTemplateLink, TemplateField } from '../components/TemplateField.js';
import { SendQuoteDialog } from '../components/SendQuoteDialog.js';
import { SendHistoryDialog } from '../components/SendHistoryDialog.js';
import { QuoteDocument, businessToDocBusiness, type QuoteDoc } from '../components/QuoteDocument.js';
import { ModalShell } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useVoiceProposal } from '../lib/useVoiceProposal.js';
import { cn } from '../lib/cn.js';

interface ItemDraft extends QuoteItemInput {
  /** Stable key for React; never sent to the server. */
  key: string;
}

type SendChannel = 'email' | 'whatsapp';

/** The ways a quote can leave the building, in the order the menu lists them. */
const SEND_CHANNELS: readonly SendChannel[] = ['email', 'whatsapp'];

let itemSeq = 0;
const newItem = (): ItemDraft => ({ key: `item-${itemSeq++}`, description: '', quantity: 1, unitPrice: 0 });

/** The filled field surface, shared by every input on this form. */
const FIELD =
  'bg-muted-foreground/10 focus:bg-muted-foreground/[0.16] transition-colors outline-none placeholder:text-muted-foreground';

/** A row worth keeping: blank trailing rows are neither saved nor templated. */
const filled = (item: QuoteItemInput): boolean => item.description.trim() !== '' || item.unitPrice > 0;

/**
 * QUO-022 — the item rows as a saved template.
 *
 * JSON rather than a delimited line per row: a description containing a tab or
 * a comma is ordinary, and a template that silently loses half a description is
 * worse than one that fails to load. Parsing is total — anything that is not a
 * list of rows comes back empty rather than throwing inside a click handler.
 */
const serializeItems = (items: ItemDraft[]): string => {
  const rows = items.filter(filled).map(({ description, quantity, unitPrice }) => ({
    description,
    quantity,
    unitPrice,
  }));
  return rows.length === 0 ? '' : JSON.stringify(rows);
};

function parseItems(body: string): ItemDraft[] {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
      .map((row) => ({
        ...newItem(),
        description: typeof row.description === 'string' ? row.description : '',
        quantity: Number(row.quantity) || 1,
        unitPrice: Number(row.unitPrice) || 0,
      }));
  } catch {
    return [];
  }
}

interface GroupedNumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'type' | 'inputMode' | 'dir'> {
  value: number;
  /** The plain number, on every key — what the form stores and the API receives. */
  onValue: (value: number) => void;
  /** Show 0 as an empty field: a price that has not been typed yet is not a price of nothing. */
  blankZero?: boolean;
}

/**
 * 1.62 — a number that shows its thousands as it is typed ("12,500").
 *
 * A text field rather than a number input, which can neither show a comma nor
 * give back what was typed. The form keeps the number; this keeps the text,
 * because the two differ while a number is being typed ("1." is 1, and
 * "0.50" is 0.5 with its zero still to be typed). The text is only rebuilt from
 * the number when the number changes from outside — a saved quote arriving,
 * "reset to 18%" — and when the field is left. Everything about what a key does
 * to the text is `groupedNumber.ts`; the caret is put back in a layout effect,
 * because regrouping the text is what moves it.
 */
export function GroupedNumberInput({ value, onValue, blankZero = false, ...props }: GroupedNumberInputProps) {
  const shown = (number: number) => (blankZero && number === 0 ? '' : formatGroupedNumber(number));
  const input = useRef<HTMLInputElement>(null);
  const [field, setField] = useState({ value, text: shown(value), caret: -1 });
  if (!Object.is(field.value, value)) setField({ value, text: shown(value), caret: -1 });

  useLayoutEffect(() => {
    if (field.caret >= 0) input.current?.setSelectionRange(field.caret, field.caret);
  }, [field]);

  return (
    <input
      {...props}
      ref={input}
      type="text"
      inputMode="decimal"
      dir="ltr"
      value={field.text}
      onChange={(event) => {
        const raw = event.target.value;
        const edit = editGroupedNumber(raw, event.target.selectionStart ?? raw.length, {
          forward: (event.nativeEvent as InputEvent).inputType === 'deleteContentForward',
        });
        const next = parseGroupedNumber(edit.text);
        setField({ value: next, text: edit.text, caret: edit.caret });
        onValue(next);
      }}
      onBlur={() => setField({ value: field.value, text: shown(field.value), caret: -1 })}
    />
  );
}

/**
 * The form's fields for a quote that is already stored: what reopening a draft
 * puts back on screen, and what a copy starts from.
 *
 * 1.16 — a copy is the same quote for another customer, so everything comes
 * across but the customer: name, phone, email and city start empty, to be typed
 * for the new person. The rest is not reconsidered, the VAT included — it is the
 * same job priced the same way, and the rate is in the summary bar for the
 * owner to change before the copy goes out.
 */
export function quoteFormValues(
  quote: Quote,
  validDaysFallback: number,
  { duplicate = false }: { duplicate?: boolean } = {},
): Record<string, unknown> {
  return {
    title: quote.title,
    galleryIds: quote.galleryIds,
    // Reopening a draft brings the details it was written with back into the
    // fields; saving writes them again, so a typo is corrected on the quote
    // being edited and nowhere else.
    customer: duplicate
      ? { name: '', phone: '', email: '', city: '' }
      : {
          ...(quote.customer?.names ? { names: quote.customer.names } : {}),
          ...(quote.customer?.phones ? { phones: quote.customer.phones } : {}),
          ...(quote.customer?.emails ? { emails: quote.customer.emails } : {}),
          name: quote.customer?.name ?? '',
          phone: quote.customer?.phone ?? '',
          email: quote.customer?.email ?? '',
          city: quote.customer?.city ?? '',
        },
    discountType: quote.discountType,
    discountValue: quote.discountValue,
    discountLabel: quote.discountLabel ?? '',
    // Kept out of the form's fields but carried through a save: a quote
    // written before the notes field was retired still prints them, and
    // dropping them here would erase text the customer may already have read.
    notes: quote.notes ?? '',
    legalText: quote.legalText ?? '',
    pricesIncludeVat: quote.pricesIncludeVat,
    // The rate the quote was written at, not today's: a document the customer
    // already approved keeps the tax it was approved with, and reopening it
    // must not quietly restate the figures at a rate that changed since.
    vatRate: quote.totals.vatRate,
    validDays: quote.validDays ?? validDaysFallback,
    followUpHours: quote.followUpHours,
    items: [],
  };
}

/**
 * QUO-001..006 — quote editor.
 *
 * Line items are reordered with explicit move buttons, never by dragging:
 * drag-and-drop reordering is excluded product-wide by the PRD.
 */
export function QuoteEditorPage({ asDialog = false }: { asDialog?: boolean } = {}) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const agentContext = agentEditorContext(params);
  const agentCopy = useAgentCopy();
  const agentRun = useQuery({ queryKey: ['agent', 'run', agentContext?.runId], queryFn: () => agentApi.get(agentContext!.runId), enabled: Boolean(agentContext), retry: false });
  const agentPrefilled = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useAuthenticated();
  const locale = currentLocale();

  const editing = Boolean(id);
  const loadedRevision = useRef<number | null>(null);
  const [revisionConflict, setRevisionConflict] = useState(false);
  const [reloadRequested, setReloadRequested] = useState(0);
  useEffect(() => {
    loadedRevision.current = null;
    setRevisionConflict(false);
  }, [id]);
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);
  /** QUO-001 — the one bin above the rows turns them into a choosing list. */
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  /**
   * QUO-007 — chosen before the quote is saved, handed to the send dialog when
   * it opens. The dialog still owns the send; this only decides what it starts
   * on, so picking a channel here never sends anything by itself.
   */
  const [channels, setChannels] = useState<SendChannel[]>(['email']);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef<HTMLButtonElement>(null);
  /** Set once a save succeeds through "send": the dialog needs a stored quote. */
  const [sending, setSending] = useState<Quote | null>(null);
  const site = useQuery({ queryKey: ['mini-site'], queryFn: () => api.get<MiniSite>('/mini-site') });

  const form = useForm(quoteWriteSchema, {
    title: '',
    customer: {},
    discountType: 'none',
    discountValue: 0,
    discountLabel: '',
    notes: '',
    legalText: '',
    pricesIncludeVat: session.business.pricesIncludeVat,
    vatRate: session.features.vatRate,
    validDays: session.features.quoteLinkTtlDays,
    followUpHours: null,
    items: [],
    galleryIds: [],
  });

  const termsRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (!termsRef.current) return;
    termsRef.current.style.height = 'auto';
    termsRef.current.style.height = `${termsRef.current.scrollHeight}px`;
  }, [form.values.legalText]);

  const existing = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<Quote>(`/quotes/${id}`),
    enabled: editing,
  });

  /*
   * 1.16 — "duplicate for another customer" lands here as `?duplicate=<id>`: a
   * new quote that starts from that one. Only a new quote reads it, so an edit
   * address with the parameter on it is still an edit. It is read by id, as the
   * list does not carry the line items, and by the key the preview and the
   * detail screen use, so a quote already opened costs nothing.
   */
  const duplicateId = editing ? null : params.get('duplicate');
  const source = useQuery({
    queryKey: ['quote', duplicateId],
    queryFn: () => api.get<Quote>(`/quotes/${duplicateId}`),
    enabled: duplicateId !== null,
  });

  const { reset } = form;
  useEffect(() => {
    if (!agentContext || !agentRun.data || agentPrefilled.current) return;
    const step = agentRun.data.steps.find(item => item.id === agentContext.stepId);
    if (!step || step.status !== 'awaiting_input' || agentRun.data.revision !== agentContext.revision) return;
    agentPrefilled.current = true;
    reset({ title: '', customer: {}, discountType: 'none', discountValue: 0, discountLabel: '', notes: '', legalText: '', pricesIncludeVat: session.business.pricesIncludeVat, vatRate: session.features.vatRate, validDays: session.features.quoteLinkTtlDays, followUpHours: null, galleryIds: [], ...step.input });
    const lines = Array.isArray(step.input.items) ? step.input.items as QuoteItemInput[] : [];
    setItems(lines.length ? lines.map(item => ({ ...item, key: `agent-${itemSeq++}` })) : [newItem()]);
  }, [agentContext, agentRun.data, reset, session.business.pricesIncludeVat, session.features]);
  const copied = useRef(false);
  useEffect(() => {
    const quote = editing ? existing.data : source.data;
    if (!quote) return;
    // Preserve unsaved fields and the revision they were loaded from.
    if (editing && loadedRevision.current !== null) return;
    if (editing) loadedRevision.current = quote.contentRevision;
    // A copy is taken once. The quote it comes from can change while the new
    // customer is being typed in (the customer opening it is a change, and the
    // live updates refetch it), and taking it again would wipe what was typed.
    if (!editing) {
      if (copied.current) return;
      copied.current = true;
    }

    reset(quoteFormValues(quote, session.features.quoteLinkTtlDays, { duplicate: !editing }));
    setItems(
      quote.items.map((item) => ({
        key: item.id,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
    );
    if (quote.discountType !== 'none') setDiscountOpen(true);
  }, [existing.data, source.data, reset, reloadRequested]);

  const { setValue } = form;
  /*
   * A new quote can arrive pre-filled: the mini-site's "attach to a quote" sends
   * the gallery's name and price this way. It is a starting point and nothing
   * more — the fields are the owner's to change, so this runs once on the way in
   * and never again, and it stays out of the way when an existing quote is being
   * edited.
   */
  useEffect(() => {
    if (editing) return;
    const title = params.get('title');
    const price = params.get('price');
    const amount = price === null ? null : Number(price);
    if (!title && amount === null) return;
    if (title) setValue('title', title);
    setItems([
      { ...newItem(), description: title ?? '', unitPrice: amount !== null && Number.isFinite(amount) ? amount : 0 },
    ]);
    // Deliberately runs on mount only: a later render must not overwrite fields
    // the owner has since typed into.
  }, []);

  /*
   * VOICE-001 — a voice note fills the form (a new quote, or `?voiceJob=`
   * from elsewhere) or edits a draft's rows. The hook holds the rules; the page
   * only places the microphone, the row marks and the customer picker.
   */
  const voice = useVoiceProposal({
    editing,
    jobId: params.get('voiceJob'),
    items,
    setItems,
    newItem,
    filled,
    setValue,
    values: form.values,
  });

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.put<Quote>(`/quotes/${id}`, quoteWritePayload(payload, loadedRevision.current)) : api.post<Quote>('/quotes', payload),
    onError: (error) => {
      if (error instanceof RequestError && error.code === 'errors.conflict') setRevisionConflict(true);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  /*
   * The preview mirrors what the server will store. The rate is Worki's, one
   * number for the whole platform, so it arrives with the session rather than
   * being read off the business — the state sets it, not the account. Whether
   * the typed prices already contain it is the one part that belongs to this
   * quote, and it is the switch in the summary bar.
   */
  const discountType = String(form.values.discountType ?? 'none') as Quote['discountType'];
  const discountValue = Number(form.values.discountValue ?? 0);
  const pricesIncludeVat = Boolean(form.values.pricesIncludeVat);
  const validDays = Number(form.values.validDays ?? session.features.quoteLinkTtlDays);
  const vatRateOnQuote = Number(form.values.vatRate ?? session.features.vatRate);
  /*
   * QUO-012 — when Worki writes the follow-up if the customer says nothing.
   * `null` is not "never": it is the business's own threshold, which is the
   * answer for most quotes and the reason the choice is four buttons rather
   * than a number to type.
   */
  const followUpHours =
    form.values.followUpHours == null ? null : Number(form.values.followUpHours);
  const toggleChannel = (option: SendChannel) =>
    setChannels((current) =>
      current.includes(option) ? current.filter((one) => one !== option) : [...current, option],
    );
  const currency = existing.data?.currency ?? session.business.currency;
  const totals = computeTotals({
    currency,
    items,
    discountType,
    discountValue,
    vatRate: vatRateOnQuote,
    pricesIncludeVat,
  });

  const customer = (form.values.customer ?? {}) as { name?: string; phone?: string; email?: string; city?: string; names?: string[]; phones?: string[]; emails?: string[] };
  const galleryIds = (form.values.galleryIds ?? []) as string[];
  const galleries = site.data?.galleries ?? existing.data?.galleries ?? [];
  const setCustomer = (patch: Partial<typeof customer>) => form.setValue('customer', { ...customer, ...patch });
  // LEAD-004 — a number that is already on someone else's card is asked about
  // on screen rather than settled underneath it; see `CustomerMatchNotice`.
  const match = useCustomerMatch(customer.phone ?? '', customer.name ?? '');

  const updateItem = (index: number, patch: Partial<ItemDraft>) => {
    // A row the owner has typed into is theirs now, not the voice note's.
    const key = items[index]?.key;
    if (key) voice.clearFlag(key);
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  /*
   * The bin above the rows, in two steps. A press that is held turns the rows
   * into a choosing list; the press that follows deletes what was ticked. A
   * plain tap opens the same list, because a control whose only way in is a
   * gesture is a control most people never find.
   *
   * `heldRef` swallows the click the browser sends after a long press: without
   * it the hold would open the list and the trailing click would close it
   * again, and the button would look broken.
   */
  const startItemPress = () => {
    heldRef.current = false;
    pressTimer.current = setTimeout(() => {
      heldRef.current = true;
      setSelectMode(true);
    }, 500);
  };

  const cancelItemPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  const toggleItem = (key: string) =>
    setSelectedItems((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onTrashClick = () => {
    if (heldRef.current) {
      heldRef.current = false;
      return;
    }
    if (!selectMode) {
      setSelectMode(true);
      return;
    }
    if (selectedItems.size > 0) {
      // Never leave the form with no row at all: an empty list has nothing to
      // type into, so a delete that would clear it leaves one blank row behind.
      setItems((current) => {
        const kept = current.filter((item) => !selectedItems.has(item.key));
        return kept.length > 0 ? kept : [newItem()];
      });
    }
    setSelectedItems(new Set());
    setSelectMode(false);
  };



  /**
   * Validates the payload that actually goes over the wire: line items live in
   * their own state, and the details are always sent as an inline customer —
   * `customerId` would pin this quote to a stored record, which is exactly what
   * the editor no longer does.
   */
  const submit = (then: (quote: Quote) => void) => {
    if (revisionConflict) return;
    // Nothing is written while the screen is still asking whose card this is.
    if (!match.settle()) return;
    return form.submit(
      async (data) => {
        if (agentContext) {
          const current = agentRun.data;
          const step = current?.steps.find(item => item.id === agentContext.stepId);
          if (!current || !step || current.revision !== agentContext.revision || step.status !== 'awaiting_input') throw new RequestError(409, 'errors.conflict');
          const resumed = await agentApi.continue(current, step, { ...step.input, ...data });
          savedRef.current = true;
          queryClient.setQueryData(['agent', 'run', resumed.id], resumed);
          navigate(`/agent?run=${encodeURIComponent(resumed.id)}`, { replace: true });
          return;
        }
        const quote = await save.mutateAsync(data);
        // Written on purpose — the exit-time draft below has nothing left to keep.
        savedRef.current = true;
        then(quote);
      },
      (values) => ({
        ...values,
        customerId: undefined,
        customer: values.customer ?? {},
        customerMode: match.mode,
        // A pristine trailing row must not block saving a draft.
        items: items.filter(filled).map(({ key: _key, ...item }) => item),
      }),
    );
  };

  const saveDraft = (event: FormEvent) => {
    event.preventDefault();
    void submit((quote) => {
      toast.success(t('common.saved'));
      navigate(`/quotes/${quote.id}`, { replace: true });
    });
  };

  /*
   * QUO-001 — a quote that was started and then left, deliberately or by
   * mistake, is kept as a draft instead of thrown away. The write happens on
   * the way out (unmount, and `pagehide` when the tab itself closes) rather
   * than on a timer: nothing is written while the form is still being typed
   * into, and the owner never races an autosave they did not ask for.
   *
   * Only a quote the server would accept is written. A customer name is the
   * one thing that cannot be guessed, so without it there is nothing to keep;
   * a title, on the other hand, is borrowed from the first line item rather
   * than invented. A quote that has already been sent or approved is left
   * alone — walking off this screen must not silently rewrite the document the
   * customer is holding.
   *
   * `fetch` directly, not the api client: `keepalive` is what makes the request
   * survive the page going away, and a failure here has no screen left to be
   * reported on.
   */
  const savedRef = useRef(false);
  const autosaveRef = useRef<() => Promise<unknown> | undefined>(() => undefined);
  autosaveRef.current = () => {
    if (revisionConflict) return;
    if (agentContext) return undefined;
    if (savedRef.current || save.isPending) return undefined;
    if (editing && (existing.data?.status ?? 'draft') !== 'draft') return undefined;

    const name = String(customer.name ?? '').trim();
    if (!name) return undefined;

    const lines = items.filter(filled).map(({ key: _key, ...item }) => item);
    const title = String(form.values.title ?? '').trim() || (lines[0]?.description ?? '').trim();
    if (!title) return undefined;

    savedRef.current = true;
    // Handed back rather than dropped: as a popup (QUO-1.4) the list is on
    // screen behind this and stays mounted, so the caller closing the dialog
    // has to wait for the write before asking the list to fetch itself again.
    return api.keepalive(editing ? `/quotes/${id}` : '/quotes', editing ? 'PUT' : 'POST', {
        ...quoteWritePayload(form.values, editing ? loadedRevision.current : null),
        title,
        customerId: undefined,
        customer,
        customerMode: match.mode,
        items: lines,
      });
  };

  useEffect(() => {
    const onHide = () => void autosaveRef.current();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
    // Mount-only: the work it does is read off a ref that every render refreshes.
  }, []);

  /**
   * QUO-1.4 — closing the popup.
   *
   * The same ✕ as before, and it still keeps the work: the draft is written on
   * the way out, as it always was. What is new is that the send history behind
   * this is the same mounted list it was before the popup opened, so it will
   * not fetch itself again on the way back — the draft just written would be
   * missing from a list that is already on screen. The write is waited for
   * first, because a refetch that overtakes it shows the list without it.
   *
   * 1.2 — and closing is a step back to the screen that opened the popup, for
   * the reasons `leave` in `signing/Compose.tsx` gives. Pushing `/quotes` left
   * the send history in the back stack twice with the form between them, so
   * the back button from the list opened the form again. A popup with nothing
   * of the app's below it gives way to the list in place. The edit screen
   * shares this ✕ but is not a popup, and is left as it was: a step back from
   * it often lands on the quote's own page, which this close does not refresh.
   */
  const closeEditor = () => {
    if (agentContext) { navigate('/agent?run=' + encodeURIComponent(agentContext.runId), { replace: true }); return; }
    const written = autosaveRef.current();
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    if (written) void written.then(refresh, refresh);
    else refresh();
    if (!asDialog) {
      navigate('/quotes');
      return;
    }
    const entry = (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof entry === 'number' && entry > 0) navigate(-1);
    else navigate('/quotes', { replace: true });
  };

  /*
   * The reference sends straight from the form, and so does this now. Worki has
   * to store the quote first — the customer opens a link to a saved record, and
   * the number is issued on write — but everything the send needs is already on
   * this screen: the channel is the picker beside the button, and the address is
   * the customer's own. A dialog that asked for both again was a second press
   * between "send" and sent, and it asked nothing new.
   *
   * The dialog is still here for the two cases that genuinely need asking: a
   * customer with no address on the chosen channel, and a send that failed —
   * where it reopens with the details, so the address can be corrected and the
   * send retried rather than the whole form done twice.
   */
  const saveAndSend = () => {
    if (channels.length === 0) {
      setOptionsOpen(true);
      return;
    }
    void submit((quote) => {
      setSending(quote);
    });
  };

  if (editing && existing.isLoading) return <LoadingScreen />;

  /*
   * 1.16 — a copy opens once the quote it is taken from has arrived. A form
   * opened empty meanwhile could be typed into, and the quote coming in would
   * then be written over it. As a popup the wait is shown inside the popup.
   */
  if (duplicateId !== null && source.isLoading) {
    return asDialog ? (
      <ModalShell open label={t('quotes.new')} onClose={closeEditor} size="wide">
        <LoadingScreen />
      </ModalShell>
    ) : (
      <LoadingScreen />
    );
  }

  if (existing.data && !quoteCanEdit(existing.data)) {
    return (
      <div className="glass-card rounded-xl p-4 space-y-3 max-w-2xl mx-auto">
        <ErrorNotice code="quotes.cannotEditSent" />
        <button
          type="button"
          onClick={() => navigate(`/quotes/${id}`)}
          className="h-9 px-3 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  const doc: QuoteDoc = {
    business: businessToDocBusiness(session.business),
    number: existing.data?.number ?? null,
    currency,
    customerName: customer.names?.filter(Boolean).join(' · ') || customer.name || '',
    customerPhone: customer.phones?.filter(Boolean).join(' · ') || customer.phone || '',
    customerEmail: customer.emails?.filter(Boolean).join(' · ') || customer.email || '',
    customerCity: customer.city ?? '',
    // 1.55 — the day the customer will read: the day the quote was sent. A draft
    // has not been, so the preview says today's, which is what they will read if
    // it goes out now. The server keeps the first send's day (`COALESCE(sent_at,
    // now)`), so a quote that has already gone out keeps showing that one.
    date: existing.data?.sentAt ?? new Date().toISOString(),
    validDays,
    title: String(form.values.title ?? ''),
    notes: String(form.values.notes ?? ''),
    legalText: String(form.values.legalText ?? ''),
    discountLabel: String(form.values.discountLabel ?? ''),
    pricesIncludeVat,
    items,
    totals,
    galleries: galleryIds.flatMap((id) => galleries.find((gallery) => gallery.id === id) ?? []),
  };

  /** A transcript kept as service text, under whatever is already there. */
  const appendToServiceDescription = (text: string) =>
    form.setValue('title', [String(form.values.title ?? ''), text].filter(Boolean).join('\n'));
  const label = (key: string) => <span className="text-xs font-semibold text-muted-foreground">{t(key)}</span>;

  /*
   * No `overflow-hidden` on the card. It was there to clip the header and the
   * footer to the rounded corners, and it also made this form the scroll
   * container for anything `sticky` inside it — a container that never scrolls,
   * so the summary bar simply travelled off the bottom of the screen with the
   * rest of the page. The two edges are rounded directly instead.
   *
   * The negative bottom margin is the second half of the same story: the page
   * reserves 6rem under itself so the fixed bottom nav never covers the last
   * row of an ordinary list, but this form ends in the summary bar, which is
   * meant to sit *on* the nav — 4rem, not 6. It gives the extra 2rem back, and
   * the bar stops with no strip of page showing underneath it.
   */
  const editor = (
    <form
      onSubmit={saveDraft}
      noValidate
      className={cn(
        'animate-fade-up flex flex-col',
        asDialog
          // The sheet is the card, and the form is what scrolls inside it: the
          // summary bar below pins to whatever scrolls, so this has to be it.
          ? 'flex-1 min-h-0 overflow-y-auto'
          : 'max-w-2xl mx-auto glass-card rounded-xl -mb-8 md:mb-0',
      )}
    >
      {/* The number this quote will carry, the date it is dated, and the way
          back to what has already gone out under it. */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0 rounded-t-xl bg-card">
        <h2 className="text-base font-semibold truncate">
          {existing.data ? t('quotes.numberShort', { number: existing.data.number }) : t('quotes.new')}
        </h2>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Only once the quote exists: there is nothing to look up before it
              has been saved, and an empty history is not what the button is for. */}
          {id && (
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="h-7 px-2 rounded-lg text-[11px] font-medium text-muted-foreground flex items-center gap-1 hover:bg-muted hover:text-foreground transition-colors"
            >
              <History className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('quotes.sendHistory')}</span>
            </button>
          )}
          <span className="text-xs text-muted-foreground ltr-nums">{formatDate(doc.date, locale)}</span>
          <button
            type="button"
            onClick={closeEditor}
            aria-label={t('common.cancel')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-4">
        {revisionConflict && <div role="alert" className="rounded-xl border border-warning/40 bg-warning/10 p-3 space-y-2">
          <p className="text-sm">{t('quotes.revisionChanged')}</p>
          <button type="button" className="text-sm text-primary underline" onClick={async () => {
            loadedRevision.current = null;
            const answer = await existing.refetch();
            if (!answer.isError) {
              setReloadRequested((value) => value + 1);
              setRevisionConflict(false);
            }
          }}>{t('quotes.reloadLatest')}</button>
        </div>}
        {form.formError && !revisionConflict && <ErrorNotice code={form.formError} />}
        {/* 1.16 — a copy whose quote could not be read opens as an empty form, and says why.
            Only a quote that never arrived: a refresh of one that did, failing
            later, is not what this form is waiting on. */}
        {source.isError && !source.data && <ErrorNotice code={(source.error as RequestError).code} />}

        {/*
          The service description is the line the customer reads first, so it is
          the first field — with the saved-text picker inside it, where the text
          it fills is, and the microphone beside it for the owner standing in a
          customer's kitchen with one hand free.
        */}
        <div className="space-y-2">
          {/* The microphone belongs to the heading, not to the box: inside the
              field it sat on top of the third line of a long description and
              was mistaken for part of the text. */}
          <div className="flex items-center justify-between gap-2">
            {label('quotes.serviceDescription')}
            <div className="flex items-center gap-1">
              <DictateButton
                value={String(form.values.title ?? '')}
                onChange={(text) => form.setValue('title', text)}
              />
              {/* VOICE-001 — the whole quote by voice, beside the one-field dictation.
                  On a draft it edits the rows it was sent; a sent quote is not
                  the note's to change. A transcript that could not become a
                  proposal stays in the service description for the owner to review. */}
              {editing && existing.data?.status === 'draft' ? (
                <VoiceButton
                  quoteId={id}
                  snapshot={items.filter(filled).map(({ description, quantity, unitPrice }) => ({ description, quantity, unitPrice }))}
                  onReady={(job) => job.proposal && voice.applyEditProposal(job.proposal)}
                  onUseAsNotes={appendToServiceDescription}
                />
              ) : !editing ? (
                <VoiceButton
                  onReady={(job) => job.proposal && voice.applyCreateProposal(job.proposal)}
                  onUseAsNotes={appendToServiceDescription}
                />
              ) : null}
            </div>
          </div>
          <div className="relative">
            <textarea
              id="q-title"
              value={String(form.values.title ?? '')}
              onChange={(event) => form.setValue('title', event.target.value)}
              placeholder={t('quotes.servicePlaceholder')}
              aria-invalid={form.errors.title ? 'true' : undefined}
              className={cn(
                'w-full h-24 p-3 pe-14 rounded-xl text-sm resize-none',
                FIELD,
                form.errors.title && 'ring-2 ring-destructive/40',
              )}
            />
            <div className="absolute top-2 end-2">
              <TemplateField
                kind="service"
                value={String(form.values.title ?? '')}
                onInsert={(text) => form.setValue('title', text)}
                label={t('quotes.serviceTemplates')}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {form.errors.title ? (
              <span className="block text-xs text-destructive">{t(form.errors.title)}</span>
            ) : (
              <span />
            )}
            <SaveTemplateLink kind="service" value={String(form.values.title ?? '')} />
          </div>
        </div>

        {/*
          Customer details belong to this quote. Picking a device contact fills
          only the fields it provides, preserving the other details already typed.

          Two to a row at every width, phone included: these are four short
          fields, and a single column turned the top of the form into a ladder.
        */}
        {/* VOICE-001 — a spoken name that more than one stored customer answers to.
            Picking fills the details as if typed; "new customer" keeps the
            spoken name already in the field. */}
        {voice.candidates && (
          <div className="space-y-2 rounded-md bg-muted-foreground/10 p-3">
            <p className="text-sm font-medium">{t('voice.pickCustomer')}</p>
            {voice.candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                className="block w-full rounded-md bg-background px-3 py-2 text-start text-sm"
                onClick={() => voice.pickCandidate(c)}
              >
                {c.name}
                {c.phone ? ` · ${c.phone}` : ''}
              </button>
            ))}
            <button type="button" className="block w-full px-3 py-2 text-start text-sm" onClick={voice.dismissCandidates}>
              {t('voice.newCustomer', { name: customer.name ?? '' })}
            </button>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{t('quotes.customer')}</p>
          <DeviceContactPicker
            onPick={(contact) => setCustomer({
              ...(contact.name ? { name: contact.name, names: [contact.name] } : {}),
              ...(contact.phone ? { phone: contact.phone, phones: [contact.phone] } : {}),
              ...(contact.email ? { email: contact.email, emails: [contact.email] } : {}),
            })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ContactValuesField label={t('customer.name')} values={customer.names ?? [customer.name ?? '']} onChange={(names) => setCustomer({ names, name: names[0] ?? '' })} />
          <ContactValuesField label={t('customer.phone')} type="tel" values={customer.phones ?? [customer.phone ?? '']} onChange={(phones) => setCustomer({ phones, phone: phones[0] ?? '' })} />
          <ContactValuesField label={t('customer.email')} type="email" values={customer.emails ?? [customer.email ?? '']} onChange={(emails) => setCustomer({ emails, email: emails[0] ?? '' })} />
          {/* The same locality field the lead form uses: suggests, never decides. */}
          <CityInput
            value={customer.city ?? ''}
            onChange={(value) => setCustomer({ city: value })}
            placeholder={t('customer.city')}
            aria-label={t('customer.city')}
            className={cn('h-9 px-3 rounded-lg text-sm min-w-0', FIELD)}
          />
        </div>
        {form.errors['customer.name'] && (
          <span className="block text-xs text-destructive">{t(form.errors['customer.name'])}</span>
        )}
        <CustomerMatchNotice state={match} name={customer.name ?? ''} />



        {/* QUO-001 — the line items, one row each. */}
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex items-center justify-between gap-2">
            {label('quotes.items')}
            <div className="flex items-center gap-1.5">
              {/* QUO-022 — a set of rows the business quotes often, saved once and
                  dropped in whole. Replaces the rows rather than appending: a
                  template is a starting point for a quote, not an addition to a
                  half-typed one. */}
              <TemplateField
                kind="items"
                value={serializeItems(items)}
                onInsert={(body) => {
                  const rows = parseItems(body);
                  if (rows.length > 0) setItems(rows);
                }}
                label={t('quotes.itemTemplates')}
              />
              {/*
                One bin for the whole list instead of one per row. A bin beside
                every line cost each row nine millimetres of width — the width
                the unit price needed — and put the only destructive control on
                the form under the thumb that is typing. It is a two-step now:
                the bin turns the rows into a choosing list, and the second
                press deletes what was chosen.
              */}
              <button
                type="button"
                onPointerDown={startItemPress}
                onPointerUp={cancelItemPress}
                onPointerLeave={cancelItemPress}
                onClick={onTrashClick}
                aria-label={
                  selectMode
                    ? selectedItems.size > 0
                      ? t('quotes.removeSelectedItems')
                      : t('quotes.doneSelecting')
                    : t('quotes.removeItems')
                }
                title={
                  selectMode
                    ? selectedItems.size > 0
                      ? t('quotes.removeSelectedItems')
                      : t('quotes.doneSelecting')
                    : t('quotes.removeItems')
                }
                className={cn(
                  'h-7 w-7 rounded-lg flex items-center justify-center transition-colors',
                  selectMode && selectedItems.size > 0
                    ? 'bg-destructive text-destructive-foreground'
                    : selectMode
                      ? 'bg-destructive/20 text-destructive'
                      : 'text-muted-foreground hover:bg-muted hover:text-destructive',
                )}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {items.map((item, index) => {
            // VOICE-001 — a row a voice note wrote, outlined until the owner edits it.
            const flag = voice.flags.get(item.key);
            return (
            <div
              key={item.key}
              className={cn(
                'flex flex-wrap gap-1.5 items-center',
                flag && 'rounded-lg',
                flag === 'mismatch' || flag === 'missing' ? 'ring-2 ring-destructive' : flag ? 'ring-2 ring-primary/50' : '',
              )}
            >
              {selectMode && (
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.key)}
                  onChange={() => toggleItem(item.key)}
                  aria-label={t('common.selectRow')}
                  className="h-4 w-4 shrink-0 accent-destructive"
                />
              )}
              <input
                type="text"
                value={item.description}
                onChange={(event) => updateItem(index, { description: event.target.value })}
                placeholder={t('quotes.description')}
                aria-label={t('quotes.description')}
                aria-invalid={form.errors[`items.${index}.description`] ? 'true' : undefined}
                className={cn(
                  'flex-1 min-w-0 h-9 px-3 rounded-lg text-sm',
                  FIELD,
                  form.errors[`items.${index}.description`] && 'ring-2 ring-destructive/40',
                )}
              />
              <GroupedNumberInput
                value={item.quantity}
                blankZero
                onValue={(quantity) => updateItem(index, { quantity })}
                placeholder={t('quotes.quantity')}
                aria-label={t('quotes.quantity')}
                className={cn('w-[22%] min-w-0 h-9 px-1 rounded-lg text-sm text-center', FIELD)}
              />
              <GroupedNumberInput
                value={item.unitPrice}
                blankZero
                onValue={(unitPrice) => updateItem(index, { unitPrice })}
                placeholder={t('quotes.unitPrice')}
                aria-label={t('quotes.unitPrice')}
                /* Match the quantity column at every viewport width. */
                className={cn('w-[22%] min-w-0 h-9 px-1 rounded-lg text-sm text-center', FIELD)}
              />
              {/* Under the row rather than inside it: the price column has no
                  width to spare for a word beside it. */}
              {flag && flag !== 'touched' && (
                <p className="w-full px-1 text-end">
                  <span className="rounded bg-muted-foreground/10 px-1.5 text-xs">
                    {t(flag === 'estimated' ? 'voice.estimated' : flag === 'missing' ? 'voice.priceMissing' : 'voice.checkAmount')}
                  </span>
                </p>
              )}
            </div>
            );
          })}
          <button
            type="button"
            onClick={() => setItems((current) => [...current, newItem()])}
            className="text-primary text-sm font-medium flex items-center gap-1.5 hover:underline"
          >
            <Plus className="h-4 w-4" />
            {t('quotes.addItem')}
          </button>
        </div>

        {/*
          QUO-003 — one text field under the items, not two.
          There were two: "notes to the customer" and "terms". Nobody could say
          which of them a payment term belonged in, and both printed as a grey
          block at the foot of the document, so they are one field with the name
          that covers both.
        */}
        <div className="space-y-2 border-t border-border pt-3">
          {label('quotes.notesTerms')}
          <div className="relative">
            <textarea
              ref={termsRef}
              id="q-legal"
              value={String(form.values.legalText ?? '')}
              onChange={(event) => form.setValue('legalText', event.target.value)}
              placeholder={t('quotes.legalPlaceholder')}
              rows={Math.max(3, String(form.values.legalText ?? '').split('\n').length + 2)}
              style={{ fieldSizing: 'content' } as React.CSSProperties}
              className={cn('w-full min-h-24 p-3 pe-14 rounded-xl text-sm resize-none overflow-hidden', FIELD)}
            />
            <div className="absolute top-2 end-2">
              <TemplateField
                kind="legal"
                value={String(form.values.legalText ?? '')}
                onInsert={(text) => form.setValue('legalText', text)}
                label={t('quotes.legalTemplates')}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <SaveTemplateLink kind="legal" value={String(form.values.legalText ?? '')} />
          </div>
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setDiscountOpen((open) => !open)}
            className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', discountOpen && 'rotate-180')} />
            {t('quotes.discount')}
          </button>
          {discountOpen && (
            <div className="grid grid-cols-2 gap-2">
              {/* The reason for a discount is the same handful of sentences in
                  every quote a business writes — "לקוח חוזר", "מזומן" — so it
                  gets the same saved-text picker the long fields have. */}
              <div className="relative min-w-0">
                <input
                  type="text"
                  value={String(form.values.discountLabel ?? '')}
                  onChange={(event) => form.setValue('discountLabel', event.target.value)}
                  placeholder={t('quotes.discountLabel')}
                  aria-label={t('quotes.discountLabel')}
                  className={cn('w-full h-9 ps-3 pe-10 rounded-lg text-sm', FIELD)}
                />
                <div className="absolute top-1/2 -translate-y-1/2 end-1.5">
                  <TemplateField
                    kind="discount"
                    value={String(form.values.discountLabel ?? '')}
                    onInsert={(text) => form.setValue('discountLabel', text)}
                    label={t('quotes.discountTemplates')}
                  />
                </div>
              </div>
              <Select
                value={discountType}
                onChange={(value) => form.setValue('discountType', value)}
                aria-label={t('quotes.discountType')}
                className={cn('h-9 px-2 min-w-0', FIELD)}
              >
                {DISCOUNT_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {t(
                      option === 'none'
                        ? 'quotes.discountNone'
                        : option === 'amount'
                          ? 'quotes.discountAmount'
                          : 'quotes.discountPercent',
                    )}
                  </option>
                ))}
              </Select>
              {discountType !== 'none' && (
                <GroupedNumberInput
                  value={discountValue}
                  blankZero
                  onValue={(value) => form.setValue('discountValue', value)}
                  placeholder={t('quotes.discountValue')}
                  aria-label={t('quotes.discountValue')}
                  className={cn('col-span-2 h-9 px-3 rounded-lg text-sm', FIELD)}
                />
              )}
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground text-center pt-1">{t('quotes.producedBy')}</p>
      </div>

      {/*
        The running total next to the buttons: the number the customer will see
        is on screen the whole time the form is being filled. It pins against
        the scroll container, whose bottom padding already clears the phone's
        navigation bar, so the total never ends up behind it.
      */}
        <details className="rounded-xl border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">{t('quotes.attachAlbums')}{galleryIds.length > 0 && ` (${galleryIds.length})`}</summary>
          <p className="text-xs text-muted-foreground mt-2">{t('quotes.selectAlbums')}</p>
          {site.isError && <ErrorNotice code="errors.network" />}
          {site.isLoading && <p className="text-xs text-muted-foreground mt-2">{t('common.loading')}</p>}
          {!site.isLoading && !site.isError && galleries.length === 0 && <p className="text-xs text-muted-foreground mt-2">{t('miniSite.noGalleries')}</p>}
          <div className="max-h-52 overflow-y-auto mt-2 space-y-1">
            {galleries.map((gallery) => <label key={gallery.id} className="flex items-center gap-2 rounded-lg p-2 hover:bg-muted text-sm cursor-pointer">
              <input type="checkbox" className="accent-primary h-4 w-4" checked={galleryIds.includes(gallery.id)} onChange={(event) => form.setValue('galleryIds', event.target.checked ? [...galleryIds, gallery.id] : galleryIds.filter((id) => id !== gallery.id))} />
              <span>{gallery.name}</span>
            </label>)}
          </div>
        </details>

      {/*
        `bottom-0` rests the bar on the scroller's content edge, which sits above
        the 6rem the page reserves for the nav — 2rem of page showed through
        underneath it. The offset spends that reserve, so the bar comes to rest
        on the nav itself. Desktop has no fixed nav and keeps the plain edge.
      */}
      <div
        className={cn(
          'shrink-0 border-t border-border p-3 space-y-2.5 sticky z-10 bg-card rounded-b-xl',
          // Inside the sheet there is no fixed nav under the scroller to spend
          // a reserve on: the bar rests on the bottom edge of the sheet itself.
          asDialog ? 'bottom-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]' : 'bottom-[-2rem] md:bottom-0',
        )}
      >
        <div className="rounded-xl bg-muted-foreground/10 p-3">
          <div className="flex items-end justify-between gap-3">
            {/*
              How the prices above are read — and the place it is changed.
              It used to be a label here and a switch in the send-options menu,
              which is a badge that looks pressable next to the figure it
              governs and does nothing when pressed. Two controls for one
              decision is also one too many, so this is the control: pressing it
              flips between prices that contain the tax and prices that have it
              added, and every figure beside it is restated.
            */}
            <button
              type="button"
              onClick={() => form.setValue('pricesIncludeVat', !pricesIncludeVat)}
              aria-pressed={pricesIncludeVat}
              disabled={totals.vatRate === 0}
              title={t('quotes.vatOnQuote')}
              className={cn(
                'h-8 px-3 rounded-lg text-[11px] font-medium flex items-center shrink-0 transition-colors',
                totals.vatRate === 0
                  ? 'bg-card border border-border text-muted-foreground'
                  : pricesIncludeVat
                    ? 'bg-primary text-primary-foreground hover:opacity-90'
                    : 'bg-primary/10 text-primary hover:bg-primary/20',
              )}
            >
              {totals.vatRate === 0
                ? t('quotes.noVat')
                : t(pricesIncludeVat ? 'quotes.pricesIncludeVat' : 'quotes.pricesPlusVat')}
            </button>
            <div className="text-end min-w-0">
              {/* What the total is made of, in the order the document prints
                  it: the rows, the discount off them, then the tax, then the
                  figure to be paid. 04.10 — the discount used to sit above the
                  rows' sum, and VAT already inside the prices read as one more
                  amount added on: 50,000 − 5,000 + 6,864 "= 45,000" looked
                  like a wrong total to Nitzan. Included VAT now says "of which". */}
              {totals.subtotal !== totals.total && (
                <p className="text-[11px] text-muted-foreground ltr-nums">
                  {t('quotes.subtotal')} {money(totals.subtotal, currency, locale)}
                </p>
              )}
              {totals.discount > 0 && (
                <p className="text-[11px] text-destructive ltr-nums">
                  {t('quotes.discount')} −{money(totals.discount, currency, locale)}
                </p>
              )}
              {totals.vat > 0 && (
                <p className="text-[11px] text-muted-foreground ltr-nums">
                  {t(pricesIncludeVat ? 'quotes.vatOfWhich' : 'quotes.vat', { rate: totals.vatRate })}{' '}
                  {money(totals.vat, currency, locale)}
                </p>
              )}
              <p className="text-xl font-bold text-green-700 dark:text-green-400 leading-tight ltr-nums">
                {money(totals.total, currency, locale)}
              </p>
            </div>
          </div>
        </div>



        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={form.submitting}
            className="h-11 flex-1 rounded-xl bg-muted text-foreground text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-accent transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {agentContext ? agentCopy('continue') : t('quotes.saveDraft')}
          </button>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            aria-label={t('quotes.preview')}
            className="h-11 w-12 shrink-0 rounded-xl border border-primary text-primary flex items-center justify-center hover:bg-primary/5 transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
          {/*
            Everything about the send, behind one icon: which ways it goes out,
            how long the link stays open, and how the prices above are to be
            read. They were three controls competing for the same strip with
            the two buttons that matter, and a dropdown 112px wide was spending
            that width to say a word an icon says.
          */}
          <button
            ref={optionsRef}
            type="button"
            onClick={() => setOptionsOpen((open) => !open)}
            aria-label={t('quotes.sendOptions')}
            aria-expanded={optionsOpen}
            title={t('quotes.sendOptions')}
            className={cn(
              'h-11 w-12 shrink-0 rounded-xl flex items-center justify-center transition-colors',
              FIELD,
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          <AnchoredMenu
            open={optionsOpen}
            onClose={() => setOptionsOpen(false)}
            anchorRef={optionsRef}
            width={248}
          >
            <div className="p-2 space-y-3">
              <div className="space-y-1">
                <span className="px-1 text-[11px] font-semibold text-muted-foreground">
                  {t('quotes.sendChannels')}
                </span>
                {/*
                  Checkboxes rather than a single choice: a customer who is
                  chased on WhatsApp and keeps the paperwork in email wants both,
                  and sending twice used to mean walking the whole form twice.
                */}
                {SEND_CHANNELS.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 px-1 py-1.5 rounded-lg text-sm cursor-pointer hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={channels.includes(option)}
                      onChange={() => toggleChannel(option)}
                      className="h-4 w-4 accent-primary"
                    />
                    {t(option === 'email' ? 'quotes.email' : 'quotes.whatsapp')}
                  </label>
                ))}
                {channels.length === 0 && (
                  <p className="px-1 text-[11px] text-destructive">{t('quotes.pickAChannel')}</p>
                )}
              </div>

              <div className="space-y-1 border-t border-border pt-2">
                <span className="px-1 text-[11px] font-semibold text-muted-foreground">
                  {t('quotes.validDays')}
                </span>
                <div className="grid grid-cols-4 gap-1">
                  {QUOTE_VALID_DAYS.map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => form.setValue('validDays', days)}
                      aria-pressed={validDays === days}
                      className={cn(
                        'h-8 rounded-lg text-xs font-medium ltr-nums transition-colors',
                        validDays === days
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-accent',
                      )}
                    >
                      {days}
                    </button>
                  ))}
                </div>
                {/*
                  The caption says which of the four is the platform's own
                  window, so a business that never thought about expiry can see
                  it is not choosing blind.
                */}
                <p className="px-1 text-[11px] text-muted-foreground">
                  {t(
                    validDays === session.features.quoteLinkTtlDays
                      ? 'quotes.validDaysDefault'
                      : 'quotes.validDaysOption',
                    { count: validDays },
                  )}
                </p>
              </div>

              {/*
                QUO-020 — the rate this quote is taxed at. Worki's national
                number is the default and stays the answer for almost every
                quote, but it is not the answer for all of them: an exempt
                dealer charges nothing, and zero-rated work is still quoted. So
                the number can be named here, and it is named once — whether the
                prices above already contain it is the switch in the summary
                bar, next to the figures it restates.
              */}
              <div className="space-y-1 border-t border-border pt-2">
                <span className="px-1 text-[11px] font-semibold text-muted-foreground">
                  {t('quotes.vatOnQuote')}
                </span>
                <div className="flex items-center gap-2 px-1">
                  <GroupedNumberInput
                    value={vatRateOnQuote}
                    onValue={(rate) => form.setValue('vatRate', rate)}
                    aria-label={t('quotes.vatRateLabel')}
                    className={cn('h-8 w-20 rounded-lg px-2 text-xs ltr-nums text-center', FIELD)}
                  />
                  <span className="text-[11px] text-muted-foreground">%</span>
                  {vatRateOnQuote !== session.features.vatRate && (
                    <button
                      type="button"
                      onClick={() => form.setValue('vatRate', session.features.vatRate)}
                      className="ms-auto text-[11px] text-primary hover:underline"
                    >
                      {t('quotes.vatRateReset', { rate: session.features.vatRate })}
                    </button>
                  )}
                </div>
                <p className="px-1 text-[11px] text-muted-foreground">
                  {vatRateOnQuote === 0 ? t('quotes.noVat') : t('quotes.vatRateHint')}
                </p>
              </div>

              {/*
                QUO-012 — the chase, on this quote's own clock.
                Worki already wrote a follow-up when a quote went unanswered,
                but on one threshold per business: the quote a customer is
                deciding on today was chased on the same clock as the one sent
                for a job next month. Sending is the moment that difference is
                known, so it is asked here, next to the send.
              */}
              <div className="space-y-1 border-t border-border pt-2">
                <span className="px-1 text-[11px] font-semibold text-muted-foreground">
                  {t('quotes.followUp')}
                </span>
                <div className="grid grid-cols-4 gap-1">
                  {QUOTE_FOLLOW_UP_HOURS.map((hours) => (
                    <button
                      key={hours ?? 'default'}
                      type="button"
                      onClick={() => form.setValue('followUpHours', hours)}
                      aria-pressed={followUpHours === hours}
                      aria-label={hours === null ? t('quotes.followUpDefault') : undefined}
                      className={cn(
                        'h-8 rounded-lg text-xs font-medium ltr-nums transition-colors',
                        followUpHours === hours
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-accent',
                      )}
                    >
                      {/*
                        The first chip is the business's own threshold, and it is
                        blank: labelled "לפי העסק" it read as a fifth option rather
                        than as the absence of one. The line under the grid still
                        says what is picked, and the name is kept for a screen reader.
                      */}
                      {hours === null ? null : t('quotes.hoursShort', { count: hours })}
                    </button>
                  ))}
                </div>
                <FollowUpInput value={followUpHours} onChange={(value) => form.setValue('followUpHours', value)} />
                <p className="px-1 text-[11px] text-muted-foreground">
                  {followUpHours === null
                    ? t('quotes.followUpDefault')
                    : t('quotes.followUpEvery', { count: followUpHours })}
                </p>
              </div>
            </div>
          </AnchoredMenu>
          {!agentContext && (          <button
            type="button"
            onClick={saveAndSend}
            disabled={form.submitting}
            className="h-11 px-4 shrink-0 rounded-xl bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity active:scale-[0.97] disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {t('quotes.sendQuote')}
          </button>)}
        </div>
      </div>

      {/* The document as the customer will read it, from the form as it stands. */}
      {/* Into `document.body`: this form sits inside `.animate-fade-up`, whose
          lingering transform would otherwise be the containing block for a
          `fixed` overlay and crop the document to the column. */}
      {previewOpen && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-foreground/25 animate-in fade-in duration-150"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreviewOpen(false);
          }}
        >
          {/* 1.55 — as wide as the customer's page (`.public-shell`, 760px on a
              desktop), so the lines break where theirs do. It was a third
              narrower. */}
          <div
            className="bg-card rounded-2xl shadow-2xl w-full max-w-[760px] max-h-[calc(92vh-4rem)] md:max-h-[92vh] mb-16 md:mb-0 overflow-y-auto animate-fade-up"
            role="dialog"
            aria-modal="true"
            aria-label={t('quotes.preview')}
          >
            {/* 1.55 — the customer's card opens on the cover band, so the close
                button floats over it instead of keeping a row of its own above
                it. The zero-height row is what holds it in the corner as the
                document scrolls. */}
            <div className="sticky top-0 z-20 h-0">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                aria-label={t('common.close')}
                className="absolute end-2 top-2 p-1.5 rounded-full bg-card/90 shadow-sm hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <QuoteDocument doc={doc} />
            <div className="p-4 border-t border-border flex justify-center sticky bottom-0 bg-card">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="h-10 px-6 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* No "send again" in the menu here: the editor may hold unsaved edits,
          and sending the copy the server still has would send something other
          than what is on screen. The editor's own send button saves first. */}
      {id && <SendHistoryDialog open={historyOpen} quoteId={id} onClose={() => setHistoryOpen(false)} />}

      {sending && (
        <SendQuoteDialog
          open
          quote={sending}
          defaultChannels={channels}
          onClose={() => setSending(null)}
          onSent={() => navigate('/quotes', { replace: true })}
        />
      )}
    </form>
  );

  /*
   * QUO-1.4 — a new quote opens over the send history rather than in place of
   * it. The form is already a card with a header of its own and a summary bar
   * along the bottom, so what it needs is the dialog around it and nothing
   * else; `Modal`'s own header would be a second one above its first.
   */
  return asDialog ? (
    <ModalShell open label={t('quotes.new')} onClose={closeEditor} size="wide">
      {editor}
    </ModalShell>
  ) : (
    editor
  );
}
